/**
 * Visual Component Registry — RUNTIME LAYER
 *
 * §2.2C STABILIZATION: Visual Registry Hierarchy (3 tiers)
 * ─────────────────────────────────────────────────────────
 * 1. generateGovernanceMapFromSeeds() → SOURCE OF TRUTH for which visuals are
 *    required/optional per family. Auto-generated from seed data at init time.
 *    Location: src/governance/pipeline/generateGovernanceMap.ts
 *
 * 2. visualRegistry.ts (THIS FILE) → RUNTIME REGISTRY mapping visual
 *    type strings to React components. Edited by developers when adding
 *    new visual components.
 *    Location: src/visuals/registry/visualRegistry.ts
 *
 * 3. VISUAL_AID_SCHEMAS.json → VALIDATION SCHEMAS defining the expected
 *    `data` shape for each visual type. Used by the governance pipeline
 *    (visualStage.ts) to validate seed content.
 *    Location: src/governance/data/VISUAL_AID_SCHEMAS.json
 *
 * Sync tool: visualRegistrySync.ts checks that (1) and (2) agree.
 *            Called during seed consolidation and available in dev diagnostics.
 *
 * Adding a new visual type:
 *   a. Create the component in src/visuals/components/
 *   b. Register it in VISUAL_REGISTRY below
 *   c. Add its type to VisualAidType in src/visuals/types.ts
 *   d. Add its schema to VISUAL_AID_SCHEMAS.json
 *   e. Add visual rules to seeds (auto-detected by generateGovernanceMapFromSeeds)
 *   f. Run visualRegistrySync check to confirm alignment
 */

import React from 'react';
import { VisualAidType } from '../types';

// Import all visual components
import { MetricStairsCompact } from '../components/MetricStairsCompact';
import { FractionVisual } from '../components/FractionVisual';
import { GeoGrid } from '../components/GeoGrid';
import { NumberLine } from '../components/NumberLine';
import { RatioTable } from '../components/RatioTable';
import { RuleCard } from '../components/RuleCard';
import { MoneyVisual } from '../components/MoneyVisual';
import { ColumnVisual } from '../components/ColumnVisual';
import { AnalogClock } from '../components/AnalogClock';
import { DigitalClock } from '../components/DigitalClock';
import { KofschipMnemonic } from '../components/KofschipMnemonic';
import { SyllableBlocks } from '../components/SyllableBlocks';
import { VerbTable } from '../components/VerbTable';
import { CircleVisual } from '../components/CircleVisual';
import { CoordinateGrid } from '../components/CoordinateGrid';
import { Box3D } from '../components/Box3D';
import { CompositeShape } from '../components/CompositeShape';
import { GeometricShape } from '../components/GeometricShape';
import { ReadingPassage } from '../components/ReadingPassage';
import { BarChartVisual } from '../components/BarChartVisual';
import { LineChartVisual } from '../components/LineChartVisual';
import { CalendarVisual } from '../components/CalendarVisual';
import { ThermometerVisual } from '../components/ThermometerVisual';
import { RulerVisual } from '../components/RulerVisual';
import { MeasuringCupVisual } from '../components/MeasuringCupVisual';
import { WeighScaleVisual } from '../components/WeighScaleVisual';
import { EqualSharingVisual } from '../components/EqualSharingVisual';
import { PercentageBar } from '../components/PercentageBar';
import { FractionStrip } from '../components/FractionStrip';
import { SplitDiagram } from '../components/SplitDiagram';
import { SentenceDiagram } from '../components/SentenceDiagram';
import { MapScale } from '../components/MapScale';
import { Protractor } from '../components/Protractor';
import { SymmetryGrid } from '../components/SymmetryGrid';
import { NumberBoard } from '../components/NumberBoard';
import { SpeedDiagram } from '../components/SpeedDiagram';
import { PriceCompare } from '../components/PriceCompare';
import { Timeline } from '../components/Timeline';
import { WorldClockPanel } from '../components/WorldClockPanel';
import { ReceiptVisual } from '../components/ReceiptVisual';
import { BudgetBar } from '../components/BudgetBar';
import { WordClassChip } from '../components/WordClassChip';
import { SignalWordCard } from '../components/SignalWordCard';
import { SynonymAntonymPair } from '../components/SynonymAntonymPair';
import { TextStructureSchema } from '../components/TextStructureSchema';
import { ReferenceArrow } from '../components/ReferenceArrow';
import { ContextSpotlight } from '../components/ContextSpotlight';
export const VISUAL_REGISTRY_IMPORT_NAMES = ['AnalogClock', 'BarChartVisual', 'Box3D', 'CalendarVisual', 'CircleVisual', 'ColumnVisual', 'CompositeShape', 'CoordinateGrid', 'DigitalClock', 'EqualSharingVisual', 'FractionStrip', 'FractionVisual', 'GeoGrid', 'GeometricShape', 'KofschipMnemonic', 'LineChartVisual', 'MapScale', 'MeasuringCupVisual', 'MetricStairsCompact', 'MoneyVisual', 'NumberLine', 'PercentageBar', 'RatioTable', 'ReadingPassage', 'RuleCard', 'RulerVisual', 'SentenceDiagram', 'SplitDiagram', 'SyllableBlocks', 'ThermometerVisual', 'VerbTable', 'WeighScaleVisual', 'Protractor', 'SymmetryGrid', 'NumberBoard', 'SpeedDiagram', 'PriceCompare', 'Timeline', 'WorldClockPanel', 'ReceiptVisual', 'BudgetBar', 'ContextSpotlight', 'ReferenceArrow', 'SignalWordCard', 'SynonymAntonymPair', 'TextStructureSchema', 'WordClassChip'] as const;

/**
 * Registry mapping visual types to their component implementations
 */
export const VISUAL_REGISTRY: Record<VisualAidType, React.ComponentType<Record<string, unknown>>> = {
  stairs: MetricStairsCompact,
  fraction: FractionVisual,
  geo: GeoGrid,
  numberline: NumberLine,
  ratio: RatioTable,
  rule: RuleCard,
  money: MoneyVisual,
  column: ColumnVisual,
  clock: AnalogClock,
  digitalClock: DigitalClock,
  decimalgrid: GeoGrid,
  verbTable: VerbTable,
  kofschipMnemonic: KofschipMnemonic,
  syllableBlocks: SyllableBlocks,
  circle: CircleVisual,
  coordinateGrid: CoordinateGrid,
  box3d: Box3D,
  compositeShape: CompositeShape,
  geometricShape: GeometricShape,
  readingPassage: ReadingPassage,
  barChart: BarChartVisual,
  lineChart: LineChartVisual,
  calendar: CalendarVisual,
  thermometer: ThermometerVisual,
  ruler: RulerVisual,
  measuringCup: MeasuringCupVisual,
  weighScale: WeighScaleVisual,
  equalSharing: EqualSharingVisual,
  percentageBar: PercentageBar,
  fractionStrip: FractionStrip,
  splitDiagram: SplitDiagram,
  sentenceDiagram: SentenceDiagram,
  mapScale: MapScale,
  protractor: Protractor,
  symmetryGrid: SymmetryGrid,
  numberBoard: NumberBoard,
  speedDiagram: SpeedDiagram,
  priceCompare: PriceCompare,
  timeline: Timeline,
  worldClockPanel: WorldClockPanel,
  receipt: ReceiptVisual,
  budgetBar: BudgetBar,
  wordClassChip: WordClassChip,
  signalWordCard: SignalWordCard,
  synonymAntonymPair: SynonymAntonymPair,
  textStructureSchema: TextStructureSchema,
  referenceArrow: ReferenceArrow,
  contextSpotlight: ContextSpotlight
};

/**
 * Get the component for a given visual type
 */
export function getVisualComponent(type: VisualAidType): React.ComponentType<Record<string, unknown>> | null {
  return VISUAL_REGISTRY[type] || null;
}